/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  TEMPORARY — DELETE AFTER MOCKUP IS COMPLETE                             ║
 * ║                                                                          ║
 * ║  This entire file is demo-only scaffolding for the auto-reverting        ║
 * ║  "active rollback" rows shown on the dashboard after a user approves a   ║
 * ║  rollback in the preview page. Remove this file and all references to   ║
 * ║  it (search for: demoActiveRollbacks) once real rollback wiring lands.   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Behavior:
 *   - Persisted in sessionStorage so the row survives navigation but resets
 *     on a hard page reload (natural demo replay semantics).
 *   - Each row carries an `expiresAt` ISO timestamp. After that time the row
 *     is filtered out, restoring the original mock rollback row for replay.
 */

import type { Migration } from '../types/migration';

const STORAGE_KEY = 'replicloud-demo-active-rollbacks';

/** Default lifetime of the synthetic "rollback in progress" row. */
export const DEMO_ROLLBACK_DURATION_MS = 20_000;

/** Prefix used on synthetic rollback row IDs so they are easy to detect. */
export const ACTIVE_ROLLBACK_ID_PREFIX = 'rollback-active-';

interface StoredActiveRollback {
    row: Migration;
    expiresAtMs: number;
}

function readRaw(): StoredActiveRollback[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((item): item is StoredActiveRollback => {
            return (
                typeof item === 'object' &&
                item !== null &&
                'row' in item &&
                'expiresAtMs' in item &&
                typeof (item as { expiresAtMs: unknown }).expiresAtMs === 'number'
            );
        });
    } catch {
        return [];
    }
}

function writeRaw(entries: StoredActiveRollback[]): void {
    if (typeof window === 'undefined') return;
    try {
        if (entries.length === 0) {
            window.sessionStorage.removeItem(STORAGE_KEY);
        } else {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
        }
    } catch {
        // sessionStorage may be unavailable; demo feature degrades silently.
    }
}

/** Append a synthetic active-rollback row that auto-expires after the default duration. */
export function addActiveRollbackRow(row: Migration, durationMs: number = DEMO_ROLLBACK_DURATION_MS): void {
    const expiresAtMs = Date.now() + durationMs;
    const entries = readRaw().filter((e) => e.expiresAtMs > Date.now());
    entries.push({ row, expiresAtMs });
    writeRaw(entries);
}

/**
 * Return the still-valid active rollback rows. Expired entries are pruned
 * from storage as a side effect so the dashboard naturally resets to the
 * original mock state.
 */
export function getActiveRollbackRows(): Migration[] {
    const now = Date.now();
    const all = readRaw();
    const valid = all.filter((e) => e.expiresAtMs > now);
    if (valid.length !== all.length) {
        writeRaw(valid);
    }
    return valid.map((e) => e.row);
}

/**
 * Return the soonest expiry timestamp (ms-since-epoch) of any active rollback
 * row, or `null` if there are none. Useful for scheduling a refetch when the
 * next row should disappear.
 */
export function getNextActiveRollbackExpiry(): number | null {
    const now = Date.now();
    const valid = readRaw().filter((e) => e.expiresAtMs > now);
    if (valid.length === 0) return null;
    return Math.min(...valid.map((e) => e.expiresAtMs));
}
